import React, { useState, useEffect } from 'react';
import { Button, Table, Modal } from 'react-bootstrap';
import { LinkContainer } from "react-router-bootstrap";
import { getCapsByBuilder, getStripeCustomer, getCustomerPaymentMethod, getLatestStoredImages, patchCapDefaultMonitor } from '../utils/bedrockAPI';
import { getUser } from '../utils/Common';
import CancelServiceModal from '../components/CancelServiceModal';
import AddCameraModal from '../components/AddCameraModal';
import TimelapseModal from '../components/TimelapseModal';
import styled from 'styled-components';
import './CameraManagement.css';

const MonitorHeading = styled.h4`
    font-family: inherit;
    font-size: 20px;
    font-weight: 500;
    margin-bottom: 15px;
`;

export default function CameraManagement(props) {
  const [caps, setCaps] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showTimelapseModal, setShowTimelapseModal] = useState(false);
  const [selectedCap, setSelectedCap] = useState(null);
  const [stripeCustomer, setStripeCustomer] = useState(null);
  const [stripeCustomerLoaded, setStripeCustomerLoaded] = useState(false);
  const [isMounted, setIsMounted] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [expandedCapId, setExpandedCapId] = useState(null);
  const [monitorImages, setMonitorImages] = useState({});

  useEffect(() => {
    loadCaps();
    if(!stripeCustomer) {
      loadStripeCustomer();
    }
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => {
      setIsMounted(false);
      window.removeEventListener('resize', checkMobile);
    };
  }, [props.match.params.builderid]);

  const loadCaps = async () => {
    try {
      const builderCaps = await getCapsByBuilder(props.match.params.builderid);
      // Filter caps for this builder
    //   const filteredCaps = builderCaps.filter(cap => 
    //     cap.builderId === getUser().builderId
    //   );
      setCaps(builderCaps);
      setIsLoading(false);
    } catch (error) {
      console.error('Error loading caps:', error);
      setIsLoading(false);
    }
  };

  const loadStripeCustomer = async () => {
    try {
      const stripeCustomer = await getStripeCustomer(props.match.params.builderid);
      console.log(stripeCustomer);
      if(isMounted) {
        setStripeCustomer(stripeCustomer);
        setStripeCustomerLoaded(true);
      }
    } catch (error) {
      console.log('No stripe connection found',error)
      if(isMounted) {
        setStripeCustomerLoaded(true);
      }
    }
  }

  useEffect(() => {
    async function loadMonitorImages() {
      if(caps.length > 0) {
        for(let i=0; i<caps.length; i++) {
          const images = await getLatestStoredImages(caps[i].capId);
          if(isMounted) {
            setMonitorImages(prev => ({
                ...prev,
                [caps[i].capId]: images
              }));
          }
        }
      }
    }
    loadMonitorImages();
    if(isMounted && caps && caps.length > 0 && !expandedCapId) {
      setExpandedCapId(caps[0].capId);
    }
  }, [caps]);

  const handleCancelClick = (cap) => {
    setSelectedCap(cap);
    setShowCancelModal(true);
  };

  const handleCapClick = async (capId) => {
    if (expandedCapId === capId) {
      setExpandedCapId(null);
      return;
    }
    setExpandedCapId(capId);
  };

  const handleMonitorSelect = async (capId, monitorId) => {
    try {
      await patchCapDefaultMonitor(capId, monitorId);
      // Update the cap in the list to reflect the new default monitor
      setCaps(prevCaps => prevCaps.map(cap => 
        cap.capId === capId 
          ? { ...cap, defaultMonitorId: monitorId }
          : cap
      ));
    } catch (error) {
      console.error('Error setting default monitor:', error);
    }
  };

  return (
    <div className="camera-management">
      <div className="header">
        <h1>Camera Management</h1>
        <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', gap: '10px' }}>
          {getUser().role === 'ADMIN' && (
            <LinkContainer to={`/accesscontrol/${props.match.params.builderid}`}>
              <Button bsStyle="default" bsSize={isMobile ? 'small' : 'large'}>
                {isMobile ? 'Back' : 'Back to Access Control'}
              </Button>
            </LinkContainer>
          )}
          <div style={{ display: 'flex', gap: '10px' }}>
            <Button 
              bsStyle="success" 
              bsSize={isMobile ? 'small' : 'large'}
              onClick={() => setShowAddModal(true)}
              disabled={!stripeCustomer}
            >
              {isMobile ? 'Order Camera' : 'Order Camera'}
            </Button>
            <LinkContainer to={`/timelapse/${props.match.params.builderid}`}>
              <Button 
                bsStyle="info" 
                bsSize={isMobile ? 'small' : 'large'}
              >
                {isMobile ? 'Timelapses' : 'View Timelapses'}
              </Button>
            </LinkContainer>
          </div>
        </div>
      </div>
      {stripeCustomerLoaded && !stripeCustomer && (
        <div className="warning-message">
          Contact Bedrock Wireless to add or cancel services as your account receives special pricing.
        </div>
      )}

      {isLoading ? (
        <div>Loading...</div>
      ) : (
        <div className="camera-list">
          {caps.map(cap => (
            <div key={cap.capId}>
              <div 
                className="camera-item"
                onClick={() => handleCapClick(cap.capId)}
              >
                <div className="camera-info">
                  <h3>{cap.name}</h3>
                </div>
                <div className="camera-actions">
                  <Button
                    bsStyle="info"
                    bsSize="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedCap(cap);
                      setShowTimelapseModal(true);
                    }}
                    style={{ marginRight: '10px' }}
                  >
                    Generate Timelapse
                  </Button>
                  <Button
                    bsStyle="danger"
                    bsSize="small"
                    disabled={!stripeCustomer}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCancelClick(cap);
                    }}
                  >
                    Cancel Service
                  </Button>
                </div>
              </div>
              {expandedCapId === cap.capId && (
                <div className="monitor-selection-panel">
                  <MonitorHeading>Select Your Hero Shot</MonitorHeading>
                  <div className="monitor-grid">
                    {monitorImages[cap.capId] && Object.entries(monitorImages[cap.capId])
                      .filter(([monitorId, monitorData]) => {
                        // For 2CAM architecture, only show monitors 1 and 2
                        if (cap.architecture && cap.architecture.includes('2CAM')) {
                          return monitorData.id <= 2;
                        }
                        return true;
                      })
                      .map(([monitorId, monitorData]) => (
                      <div 
                        key={monitorId}
                        className={`monitor-option ${cap.defaultMonitorId === monitorId ? 'selected' : ''}`}
                        onClick={() => handleMonitorSelect(cap.capId, monitorId)}
                      >
                        <img src={monitorData.url} alt={`Monitor ${monitorData.id}`} />
                        <div className="monitor-label">
                          Monitor {monitorData.id}
                          {cap.defaultMonitorId === monitorId && ' (Default)'}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {stripeCustomer && <CancelServiceModal
        show={showCancelModal}
        onHide={() => setShowCancelModal(false)}
        cap={selectedCap}
        stripeCustomer={stripeCustomer}
        builderId={props.match.params.builderid}
      />}

      {stripeCustomer && <AddCameraModal
        show={showAddModal}
        onHide={() => setShowAddModal(false)}
        stripeCustomer={stripeCustomer}
        builderId={props.match.params.builderid}
      />}

      {selectedCap && <TimelapseModal
        show={showTimelapseModal}
        onHide={() => setShowTimelapseModal(false)}
        cap={selectedCap}
        monitorImages={monitorImages[selectedCap.capId]}
      />}
    </div>
  );
} 